import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import {
  Activity, Droplet, Heart, Calendar, Sun, FlaskConical, ShieldAlert, Apple,
  ArrowLeft, ShieldCheck, Loader2,
} from "lucide-react";
import SEO from "@/components/SEO";
import AppFooter from "@/components/AppFooter";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Public Health Assessment Page (Iter 260)
 *
 * 8 calculators — no login required, results are computed server-side and
 * tracked anonymously for stats. EU AI Act compliant: framed as educational
 * tools, not medical diagnoses.
 */
export default function HealthAssessmentPage() {
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const bg = language === "bg";
  const [activeCalc, setActiveCalc] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const CALCULATORS = bg ? [
    { id: "bmi", icon: Activity, color: "#10B981", title: "ИТМ (BMI)", desc: "Класификация на телесна маса" },
    { id: "vitamin-d", icon: Sun, color: "#F59E0B", title: "Витамин D", desc: "Препоръчителен прием" },
    { id: "water-intake", icon: Droplet, color: "#0EA5E9", title: "Прием на вода", desc: "Дневна нужда" },
    { id: "period", icon: Calendar, color: "#EC4899", title: "Менструален цикъл", desc: "Овулация и предсказания" },
    { id: "egfr", icon: FlaskConical, color: "#8B5CF6", title: "eGFR (бъбречна функция)", desc: "CKD-EPI 2021" },
    { id: "homa-ir", icon: Heart, color: "#EF4444", title: "HOMA-IR", desc: "Инсулинова резистентност" },
    { id: "cvd-risk", icon: ShieldAlert, color: "#DC2626", title: "Сърдечно-съдов риск", desc: "10-годишна оценка" },
    { id: "diabetes-risk", icon: Apple, color: "#7C3AED", title: "Риск от диабет", desc: "FINDRISC скоринг" },
  ] : [
    { id: "bmi", icon: Activity, color: "#10B981", title: "BMI", desc: "Body mass classification" },
    { id: "vitamin-d", icon: Sun, color: "#F59E0B", title: "Vitamin D", desc: "Recommended intake" },
    { id: "water-intake", icon: Droplet, color: "#0EA5E9", title: "Water intake", desc: "Daily need" },
    { id: "period", icon: Calendar, color: "#EC4899", title: "Menstrual cycle", desc: "Ovulation & predictions" },
    { id: "egfr", icon: FlaskConical, color: "#8B5CF6", title: "eGFR (kidney)", desc: "CKD-EPI 2021" },
    { id: "homa-ir", icon: Heart, color: "#EF4444", title: "HOMA-IR", desc: "Insulin resistance" },
    { id: "cvd-risk", icon: ShieldAlert, color: "#DC2626", title: "CVD risk", desc: "10-year score" },
    { id: "diabetes-risk", icon: Apple, color: "#7C3AED", title: "Diabetes risk", desc: "FINDRISC scoring" },
  ];

  const sectionBg = isDark ? "bg-[hsl(223,47%,6%)]" : "bg-[#F9F9F7]";
  const cardBg = isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : "bg-white border-slate-100";
  const txt = isDark ? "text-white" : "text-[#1A1A2E]";
  const subTxt = isDark ? "text-slate-400" : "text-slate-500";

  const handleSubmit = async (calcId, body) => {
    setLoading(true);
    setResult(null);
    try {
      const r = await axios.post(`${API}/public/calculators/${calcId}`, body);
      setResult(r.data);
    } catch (e) {
      setResult({ error: e?.response?.data?.detail || "Грешка" });
    } finally {
      setLoading(false);
    }
  };

  const reset = () => { setActiveCalc(null); setResult(null); };

  return (
    <div className={`min-h-screen ${sectionBg}`} data-testid="health-assessment-page">
      <SEO
        title={bg ? "Здравна оценка — безплатни калкулатори" : "Health Assessment — free calculators"}
        path="/health-assessment"
        description={bg
          ? "Безплатни здравни калкулатори: BMI, витамин D, бъбречна функция, инсулинова резистентност, риск от диабет и сърдечно-съдов риск. Без регистрация."
          : "Free health calculators: BMI, vitamin D, kidney function, insulin resistance, diabetes & CVD risk. No login required."}
      />

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Header */}
        <button
          onClick={() => navigate("/")}
          className={`inline-flex items-center gap-1.5 mb-4 text-sm ${subTxt} hover:opacity-80`}
          data-testid="back-to-home-btn"
        >
          <ArrowLeft className="w-4 h-4" /> {bg ? "Към началото" : "Back to home"}
        </button>

        <h1 className={`text-3xl sm:text-4xl font-bold mb-2 ${txt}`}>
          {bg ? "Здравна оценка" : "Health Assessment"}
        </h1>
        <p className={`text-sm sm:text-base mb-2 ${subTxt}`}>
          {bg
            ? "8 безплатни калкулатора — без регистрация, без email. Резултатите се изчисляват веднага."
            : "8 free calculators — no signup, no email. Results computed instantly."}
        </p>

        {/* Compliance banner */}
        <div className={`rounded-lg p-3 mb-6 border text-[11.5px] flex items-start gap-2 ${
          isDark ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-100" : "bg-emerald-50 border-emerald-200 text-emerald-900"
        }`}>
          <ShieldCheck className="w-4 h-4 mt-0.5 flex-shrink-0 text-emerald-500" />
          <span>
            {bg
              ? "Образователен инструмент · EU AI Act (ограничен риск) · Не предоставя медицинска диагноза. За клинично решение — консултирайте се с лекар."
              : "Educational tool · EU AI Act (limited risk) · Not a medical diagnosis. Consult a doctor for clinical decisions."}
          </span>
        </div>

        {/* Active calculator OR grid */}
        {activeCalc ? (
          <CalculatorForm
            calculator={CALCULATORS.find((c) => c.id === activeCalc)}
            onSubmit={handleSubmit}
            onReset={reset}
            result={result}
            loading={loading}
            bg={bg}
            isDark={isDark}
          />
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="calculators-grid">
            {CALCULATORS.map((c) => {
              const Icon = c.icon;
              return (
                <button
                  key={c.id}
                  onClick={() => { setActiveCalc(c.id); setResult(null); }}
                  className={`text-left rounded-xl p-4 border transition-all hover:scale-[1.02] hover:shadow-lg ${cardBg}`}
                  data-testid={`calculator-tile-${c.id}`}
                >
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-3"
                       style={{ backgroundColor: c.color + "22" }}>
                    <Icon className="w-5 h-5" style={{ color: c.color }} />
                  </div>
                  <h3 className={`text-sm font-bold mb-0.5 ${txt}`}>{c.title}</h3>
                  <p className={`text-[11px] ${subTxt}`}>{c.desc}</p>
                </button>
              );
            })}
          </div>
        )}
      </div>
      <AppFooter />
    </div>
  );
}

function CalculatorForm({ calculator, onSubmit, onReset, result, loading, bg, isDark }) {
  const [form, setForm] = useState({});
  const Icon = calculator.icon;

  // Field configs per calculator
  const FIELDS = {
    bmi: [
      { name: "height", label: bg ? "Височина (cm)" : "Height (cm)", type: "number", default: 170 },
      { name: "weight", label: bg ? "Тегло (kg)" : "Weight (kg)", type: "number", default: 70 },
    ],
    "vitamin-d": [
      { name: "current_level", label: bg ? "Текущо ниво (ng/mL)" : "Current level (ng/mL)", type: "number", default: 20 },
      { name: "weight", label: bg ? "Тегло (kg)" : "Weight (kg)", type: "number", default: 70 },
    ],
    "water-intake": [
      { name: "weight", label: bg ? "Тегло (kg)" : "Weight (kg)", type: "number", default: 70 },
      { name: "activity", label: bg ? "Активност" : "Activity", type: "select",
        options: [["sedentary","Заседнал"],["light","Лека"],["moderate","Умерена"],["active","Активна"],["athletic","Спортна"]],
        default: "moderate" },
      { name: "climate", label: bg ? "Климат" : "Climate", type: "select",
        options: [["cold","Студен"],["temperate","Умерен"],["warm","Топъл"],["hot","Горещ"]],
        default: "temperate" },
    ],
    period: [
      { name: "lastPeriodDate", label: bg ? "Първи ден на последна менструация" : "First day of last period", type: "date", default: new Date().toISOString().split("T")[0] },
      { name: "cycleLength", label: bg ? "Дължина на цикъл (дни)" : "Cycle length (days)", type: "number", default: 28 },
      { name: "periodDuration", label: bg ? "Продължителност на менструация (дни)" : "Period duration (days)", type: "number", default: 5 },
    ],
    egfr: [
      { name: "creatinine_mg_dl", label: bg ? "Креатинин (mg/dL)" : "Creatinine (mg/dL)", type: "number", default: 1.0, step: 0.1 },
      { name: "age", label: bg ? "Възраст" : "Age", type: "number", default: 40 },
      { name: "sex", label: bg ? "Пол" : "Sex", type: "select",
        options: [["male", bg?"Мъж":"Male"],["female", bg?"Жена":"Female"]], default: "male" },
    ],
    "homa-ir": [
      { name: "glucose_mg_dl", label: bg ? "Глюкоза (mg/dL)" : "Glucose (mg/dL)", type: "number", default: 90 },
      { name: "insulin_uiu_ml", label: bg ? "Инсулин (μIU/mL)" : "Insulin (μIU/mL)", type: "number", default: 8 },
    ],
    "cvd-risk": [
      { name: "age", label: bg ? "Възраст" : "Age", type: "number", default: 40 },
      { name: "smoking", label: bg ? "Пушене" : "Smoking", type: "checkbox", default: false },
      { name: "diabetes", label: bg ? "Диабет" : "Diabetes", type: "checkbox", default: false },
      { name: "hypertension", label: bg ? "Хипертония" : "Hypertension", type: "checkbox", default: false },
    ],
    "diabetes-risk": [
      { name: "age", label: bg ? "Възраст" : "Age", type: "number", default: 40 },
      { name: "bmi", label: bg ? "BMI" : "BMI", type: "number", default: 25, step: 0.1 },
      { name: "waist_cm", label: bg ? "Обиколка на талия (cm)" : "Waist (cm)", type: "number", default: 90 },
      { name: "family_history", label: bg ? "Семейна обремененост за диабет" : "Family history of diabetes", type: "checkbox", default: false },
    ],
  };

  const fields = FIELDS[calculator.id] || [];

  React.useEffect(() => {
    const initial = {};
    fields.forEach((f) => { initial[f.name] = f.default; });
    setForm(initial);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [calculator.id]);

  const submit = (e) => {
    e.preventDefault();
    onSubmit(calculator.id, form);
  };

  const cardBg = isDark ? "bg-[hsl(223,47%,12%)] border-[hsl(223,47%,20%)]" : "bg-white border-slate-100";
  const txt = isDark ? "text-white" : "text-[#1A1A2E]";
  const subTxt = isDark ? "text-slate-400" : "text-slate-500";

  return (
    <Card className={cardBg} data-testid={`calculator-form-${calculator.id}`}>
      <CardContent className="p-6">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-10 h-10 rounded-lg flex items-center justify-center"
               style={{ backgroundColor: calculator.color + "22" }}>
            <Icon className="w-5 h-5" style={{ color: calculator.color }} />
          </div>
          <div className="flex-1">
            <h2 className={`text-lg font-bold ${txt}`}>{calculator.title}</h2>
            <p className={`text-[11px] ${subTxt}`}>{calculator.desc}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={onReset} data-testid="calc-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> {bg ? "Назад" : "Back"}
          </Button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          {fields.map((f) => (
            <div key={f.name}>
              <Label className={`text-sm font-semibold ${isDark ? "text-slate-200" : "text-slate-700"}`}>
                {f.label}
              </Label>
              {f.type === "select" ? (
                <select
                  value={form[f.name] ?? f.default}
                  onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  className={`mt-1 w-full h-10 px-3 rounded-md border text-sm ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-white" : "bg-white border-slate-200"}`}
                  data-testid={`calc-input-${f.name}`}
                >
                  {f.options.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              ) : f.type === "checkbox" ? (
                <label className="flex items-center gap-2 mt-1">
                  <input
                    type="checkbox"
                    checked={!!form[f.name]}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.checked })}
                    data-testid={`calc-input-${f.name}`}
                    className="w-4 h-4"
                  />
                  <span className={`text-sm ${subTxt}`}>{f.label}</span>
                </label>
              ) : (
                <Input
                  type={f.type}
                  step={f.step || "any"}
                  value={form[f.name] ?? f.default}
                  onChange={(e) => setForm({ ...form, [f.name]: f.type === "number" ? parseFloat(e.target.value) : e.target.value })}
                  data-testid={`calc-input-${f.name}`}
                  className={`mt-1 ${isDark ? "bg-[hsl(223,47%,10%)] border-[hsl(223,47%,20%)] text-white" : ""}`}
                />
              )}
            </div>
          ))}
          <Button type="submit" disabled={loading} className="w-full mt-4" data-testid="calc-submit-btn"
                  style={{ backgroundColor: calculator.color }}>
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
            {bg ? "Изчисли" : "Calculate"}
          </Button>
        </form>

        {/* Result */}
        {result && (
          <div
            className={`mt-5 rounded-lg p-4 border ${isDark ? "bg-[hsl(223,47%,8%)] border-[hsl(223,47%,18%)]" : "bg-slate-50 border-slate-100"}`}
            data-testid={`calc-result-${calculator.id}`}
          >
            {result.error ? (
              <p className="text-sm text-rose-500">{result.error}</p>
            ) : (
              <>
                <p className={`text-xs uppercase font-bold mb-2 ${subTxt}`}>{bg ? "Резултат" : "Result"}</p>
                <p className={`text-xl font-bold mb-1 ${txt}`} style={{ color: result.color }}>
                  {result.label_bg || result.label_en || "—"}
                </p>
                <pre className={`mt-2 text-[10px] overflow-x-auto p-2 rounded ${isDark ? "bg-black/40 text-slate-400" : "bg-white text-slate-500"}`}>
{JSON.stringify(result, null, 2)}
                </pre>
              </>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
